import * as React from 'react';
import { connect } from 'react-redux';
import { onContactFormSubmit, updateContactFormData } from '../../actions/contactFormActions';
import { __ } from '../../helpers/TranslationService';
import { validateEmail } from '../../helpers/validation';
import { IContactFormData, IContactFormReducer } from '../../interfaces/IContactForm';
import { submitContactForm } from '../../reducers/contactForm/contactFormMappers';
import { IPageDataReducer } from '../../reducers/pageData';
import { TrackComponent } from '../TrackComponent';
import FormSent from './FormSent';
import { Input } from './Input';
import { PhoneSelection } from './PhoneSelection';
import { SubmitButton } from './SubmitButton';
import { Textarea } from './Textarea';
import DynamicForm from '../DynamicForm/DynamicForm';

interface StateProps {
    pageData: IPageDataReducer;
    contactForm: IContactFormReducer;
    channel: string;
    advisor?: {
        name: string;
        phone: string;
        companies: string;
        languages: string;
    };
}

interface DispatchProps {
    updateContactFormData(keyAndValue: { channel; key; value }): void;
    onContactFormSubmit(channel: string): void;
}

type IProps = StateProps & DispatchProps;

export class ContactFormClass extends React.Component<IProps, any> {
    componentDidMount() {
        if (this.props.advisor?.name) {
            const advisor = this.props.advisor;
            this.props.updateContactFormData({
                channel: this.props.channel,
                key: 'stafferName',
                value: advisor.name,
            });
            this.props.updateContactFormData({
                channel: this.props.channel,
                key: 'stafferPhone',
                value: advisor.phone,
            });
            this.props.updateContactFormData({
                channel: this.props.channel,
                key: 'stafferCompanyName',
                value: advisor.companies,
            });
            this.props.updateContactFormData({
                channel: this.props.channel,
                key: 'stafferLanguage',
                value: advisor.languages,
            });
        }
    }

    handleInputChange = (key) => (event) => {
        this.props.updateContactFormData({
            channel: this.props.channel,
            key,
            value: event.target.value,
        });
    };

    handlePhoneChange = (event) => {
        let phone = event.target.value && event.target.value.replace(/[^\d+]+/g, '');
        if (this.props.contactForm[this.props.channel].phoneNumber === phone) return;
        this.props.updateContactFormData({
            channel: this.props.channel,
            key: 'phoneNumber',
            value: phone,
        });
    };

    handleCountryCodeChange = (value) => {
        this.props.updateContactFormData({
            channel: this.props.channel,
            key: 'countryCode',
            value,
        });
    };

    handleError = (
        isEmailError: boolean,
        isSubmitted: boolean,
        isSubmitError: boolean,
        isContactFormValid: boolean,
        messageError: string,
    ) => {
        let errorMessage = '';
        if (isEmailError) errorMessage = __('errors.email', 'dreamlines');
        if (isSubmitted && !isContactFormValid)
            errorMessage = __('mobile.errors.validationError', 'dreamlines');
        if (isSubmitError) errorMessage = messageError;
        return errorMessage && <div className="contact-form_errorBox">{errorMessage}</div>;
    };

    cleanupString = (value) => value.replace(/(<p[^>]+?>|<p>|<\/p>)/gim, '');
    getApiErrors = (errors) =>
        errors.map((error) => <div key={error}>- {__(error, 'dreamlines')}</div>);
    render() {
        const { pageData, contactForm } = this.props;

        const contactFormChannel: IContactFormData = contactForm[this.props.channel];
        const {
            isSubmitted,
            isSent,
            isSubmitError,
            firstInvalidFormField,
            firstName,
            lastName,
            countryCode,
            phoneNumber,
            customerMail,
            messageBody,
            errors,
        } = contactFormChannel;

        if (isSent) return <FormSent />;

        const isEmailError = isSubmitted && !validateEmail(customerMail);
        const isContactFormValid = submitContactForm(contactFormChannel).isValid;

        const TrackableButton = TrackComponent(SubmitButton);

        const messageError = this.getApiErrors(errors);

        const ErrorBar = this.handleError(
            isEmailError,
            isSubmitted,
            isSubmitError,
            isContactFormValid,
            messageError,
        );

        const firstnameProps = {
            placeholder: `${__('personalData.firstname', 'dreamlines')} *`,
            value: firstName,
            onChange: this.handleInputChange('firstName'),
            isError: isSubmitted && firstName.length === 0,
            isFirstInvalidFormField: firstInvalidFormField === 'firstName',
        };
        const lastnameProps = {
            placeholder: `${__('personalData.lastname', 'dreamlines')}`,
            value: lastName,
            onChange: this.handleInputChange('lastName'),
            isError: isSubmitted && lastName.length === 0,
            isFirstInvalidFormField: firstInvalidFormField === 'lastName',
        };
        const emailProps = {
            placeholder: __('personalData.email', 'dreamlines'),
            value: customerMail,
            onChange: this.handleInputChange('customerMail'),
            type: 'email',
            isError: isEmailError,
        };
        const messageProps = {
            placeholder: __('personalData.message', 'dreamlines'),
            value: messageBody,
            onChange: this.handleInputChange('messageBody'),
            rows: 3,
        };
        const phoneSelectionProps = {
            phoneNumber,
            isSubmitted,
            placeholder: `${__('personalData.phone', 'dreamlines')}`,
            ddd: null,
            countryCode: countryCode,
            countryCodes: pageData.brandConfig.requestForm.countryCodes,
            onPhoneChange: this.handlePhoneChange,
            onDDDChange: this.handleInputChange('ddd'),
            onCountryCodeChange: this.handleCountryCodeChange,
            portalId: pageData.appConfig.portalId,
            firstInvalidFormField: firstInvalidFormField,
        };
        const headline = `${__(
            'We will gladly make you an offer taking into account all valid benefits and discounts.',
            'dreamlines',
        )}

        ${__('Get in Contact with us!', 'dreamlines')}
        `;
        return (
            <div className="contact-form-container">
                <div className="contact-form">
                    <h3 className="contact-form_headline">{headline}</h3>
                    <span className="contact-form_requiredFieldsText">
                        {`* ${__('required fields', 'dreamlines')}`}
                    </span>
                    <div className="contact-form_form">
                        <div className="contact-form_form_inputGroup">
                            <Input {...firstnameProps} />
                            <Input {...lastnameProps} />
                            <PhoneSelection {...phoneSelectionProps} />
                            <Input {...emailProps} />
                        </div>
                        <div className="contact-form_form_textWrapper">
                            <Textarea {...messageProps} />
                        </div>
                    </div>

                    {ErrorBar}

                    <div className="contact-form_btnWrapper">
                        <TrackableButton
                            onClick={() => this.props.onContactFormSubmit(this.props.channel)}
                            channel={this.props.channel}
                            isFormValid={isContactFormValid}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: StateProps) => state;
const mapDispatchToProps = { updateContactFormData, submitContactForm, onContactFormSubmit };

export const ContactForm = connect<StateProps, DispatchProps, any>(
    mapStateToProps,
    mapDispatchToProps,
)(ContactFormClass);

export default ContactForm;
